import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { string, func } from 'prop-types';
import {
  DrawerBody,
  DrawerFooter,
  Typography,
  DrawerHeader,
  LoadingButton
} from '@one-thd/sui-atomic-components';
import { useStore } from "@thd-nucleus/experience-context";
import {
  params, useDataModel, useLazyDataModel, extend, QueryProvider,
  arrayOf as arrayOfType,
  string as stringType,
  number as numberType,
  bool as boolType,
  shape as shapeType, hoist
} from "@thd-nucleus/data-sources";
import { NOT_FETCHED, OPERATION_ADD_ITEMS, SCREENS, TRACK } from "../../AddToConstants";
import { AddToContext } from "../../AddToContext";
import { MiniListsSummary } from "./mini-list-summary/MiniListsSummary";
import { MiniProductList } from "./mini-product-list/MiniProductList";
import { useUpdateList, updateListModel } from "../../hooks/useLists";
import { AbandonSelectionWarning } from "./AbandonSelectionWarning";
import { AnalyticsPriceFetcher } from "./AnalyticsPriceFetcher";
import { triggerAnalytics } from "../../AddToHelpers";

const HoistedAnalyticsPriceFetcher = hoist(AnalyticsPriceFetcher, MiniListsSummary, MiniProductList)

export const CopyList = ({ listId, onClose, onMaterialsAdded }) => {
  const [selectedList, setSelectedList] = useState(null);
  const [selectedListItems, setSelectedListItems] = useState({});
  const [itemsWithPricing, setItemsWithPricing] = useState({}); // Store items with pricing data
  const [page, setPage] = useState(1);
  const {
    setScreen,
    screen,
    setSuccessMessage,
    abandonWarning,
    setAbandonWarning,
    canShowWarning,
    setCanShowWarning
  } = useContext(AddToContext);
  const store = useStore({ varnish: true, online: false });
  const { membershipInformation } = store;

  const {
    data, error, loading: listsLoading, refetch, client
  } = useDataModel('lists', {
    variables: {
      page: page || 1,
      pageSize: 12
    },
    context: { withAuth: true }
  });

  const { lists } = data || {};
  const pagination = lists?.pagination || {};
  const { totalResults } = pagination;

  const { updateList, updateListResponse } = useUpdateList({
    id: listId,
    operationName: OPERATION_ADD_ITEMS,
  });

  const { data: addToListData, loading } = updateListResponse || {};

  const totalItemsSelected = useMemo(() => {
    return Object.values(selectedListItems).reduce((acc, items) => acc + items.length, 0);
  }, [selectedListItems]);

  useEffect(() => {
    return () => {
      setCanShowWarning(false);
    }
  }, []);

  const showButtonLoading = useMemo(() => {
    if (loading) return true;
    const selectedProductsToAdd = Object.values(selectedListItems).flat();
    return selectedProductsToAdd.some(item => item.basePrice === NOT_FETCHED);
  }, [selectedListItems, loading]);

  useEffect(() => {
    const shouldShowWarning = Boolean(totalItemsSelected);
    if (canShowWarning !== shouldShowWarning) {
      setCanShowWarning(shouldShowWarning);
    }
  }, [totalItemsSelected]);

  const prepareAddToListItems = (addBasePrice = false) => {
    const selectedProductsToAdd = Object.values(selectedListItems).flat();
    let productsToAdd = [];
    if (selectedProductsToAdd?.length > 0) {
      const itemMap = new Map();
      selectedProductsToAdd.forEach(item => {
        if (itemMap.has(item.itemId)) {
          const existingItem = itemMap.get(item.itemId);
          const modifiedItem = { ...existingItem, quantity: existingItem.quantity + item.quantity };
          itemMap.set(item.itemId, modifiedItem);
        } else {
          itemMap.set(item.itemId, item);
        }
      });
      productsToAdd = Array.from(itemMap.entries()).map(([itemId, item]) => {
        if (addBasePrice) {
          return item;
        } else {
          return {
            itemId,
            quantity: item.quantity,
          };
        }
      });
    }
    return productsToAdd;
  };

  useEffect(() => {
    if (addToListData) {
      // Calculate analytics data before submitting using itemsWithPricing instead of selectedListItems
      let entireListCount = 0;
      let partialItemCount = 0;

      // Calculate statistics for each list based on itemsWithPricing
      Object.entries(selectedListItems).forEach(([listId, items]) => {
        if (items.length > 0) {
          // Find the current list to check if entire list is selected
          const currentList = lists?.results?.find(list => list.id === listId);
          if (currentList && currentList.itemCount === items.length) {
            entireListCount += 1;
          } else {
            partialItemCount += 1;
          }
        }
      });

      // Build location string according to specification
      const locationParts = [];
      if (entireListCount > 0) {
        locationParts.push(`entire>${entireListCount}`);
      }
      if (partialItemCount > 0) {
        locationParts.push(`partial>${partialItemCount}`);
      }
      const location = `existing list: copy list: ${locationParts.join(', ')}`;

      // Format items for analytics using itemsWithPricing which has pricing data
      const itemsWithPrice = prepareAddToListItems(true);
      const items = itemsWithPrice.map(item => ({
        quantity: item.quantity,
        sku: item.itemId,
        price: item.basePrice,
      }));
      // 6.13.1 Add to List action
      triggerAnalytics(TRACK.ADD_TO_LIST_ADD_ACTION, {
        location,
        items,
        isBulkAction: false
      });
      setScreen(SCREENS.DEFAULT);
      const productsAdded = Object.values(selectedListItems).flat();
      setSuccessMessage(`${productsAdded?.length} items successfully added to list`);
      setSelectedListItems({});
      setItemsWithPricing({});
      setSelectedList(null);
      onMaterialsAdded();
    }
  }, [addToListData, itemsWithPricing, lists]);

  const onListClick = (currentList) => {
    if (currentList) {
      // 7.5 Mini List Details Overlay
      triggerAnalytics(TRACK.ADD_TO_LIST_MINI_LIST_DETAILS_OVERLAY);
      const { id, name, itemCount } = currentList;
      setSelectedList({ listId: id, name, totalItems: itemCount });
      setScreen(SCREENS.COPY_LIST_PRODUCTS);
    }
  }

  const onBackPress = (__event, overrideWarning = false) => {
    if (screen === SCREENS.COPY_LIST_PRODUCTS) {
      setScreen(SCREENS.COPY_LIST_SUMMARY);
      setSelectedList(null);
      // 7.6 Mini List Details Overlay: Back button
      triggerAnalytics(TRACK.ADD_TO_LIST_MINI_LIST_DETAILS_BACK_PRESS);
    } else {
      const showWarning = canShowWarning && !overrideWarning;
      if (showWarning) {
        setAbandonWarning({ show: true, action: 'BACK' })
      } else {
        setScreen(SCREENS.DEFAULT);
      }
    }
  }

  const onItemSelectionChange = (item) => {
    const { listId: currentListId, itemId, quantity, basePrice } = item;
    if (loading) return false;
    const currentListItems = selectedListItems[currentListId] || [];
    const isThisItemSelected = currentListItems.some(item => item.itemId === itemId);
    if (isThisItemSelected) {
      setSelectedListItems(prevItems => ({
        ...prevItems,
        [currentListId]: currentListItems.filter(item => item.itemId !== itemId)
      }));
    }
    else {
      setSelectedListItems(prevItems => ({
        ...prevItems,
        [currentListId]: [...currentListItems, { itemId, quantity, basePrice }]
      }));
    }
  }

  const onListSelectAll = (currentListId, items) => {
    if (loading) return false;
    const currentListItems = selectedListItems[currentListId] || [];
    if (currentListItems.length === items.length) {
      // If all items are already selected, deselect all
      setSelectedListItems(prevItems => ({
        ...prevItems,
        [currentListId]: []
      }));
    } else {
      setSelectedListItems(prevItems => ({
        ...prevItems,
        [currentListId]: items.map(item => ({
          itemId: item.itemId, quantity: item.quantity, basePrice: item.basePrice
        }))
      }));
    }
  };

  const handleSubmit = () => {
    const productsToAdd = prepareAddToListItems();
    if (productsToAdd?.length > 0) {
      updateList({
        variables: {
          products: productsToAdd
        }
      });
    }
  }

  const onDrawerClose = (__event, overrideWarning = false) => {
    const showWarning = canShowWarning && !overrideWarning;
    if (showWarning) {
      setAbandonWarning({ show: true, action: 'CLOSE' });
    } else {
      onClose();
    }
  }

  const updateBasePrice = (itemBasePriceMap) => {
    setSelectedListItems(prevItems => {
      const updatedSelectedListItems = {};
      Object.keys(prevItems).forEach((listid) => {
        updatedSelectedListItems[listid] = prevItems[listid]?.map(item => {
          if (item.basePrice === NOT_FETCHED) {
            const basePrice = itemBasePriceMap.get(item.itemId) || NOT_FETCHED;
            return { ...item, basePrice };
          }
          return item;
        });
      });
      return updatedSelectedListItems;
    });
  }

  return (
    <>
      <DrawerHeader
        data-testid="add-to-drawer-header"
        title={`Copy from ${selectedList?.name || 'Your Lists'}`}
        onClose={onDrawerClose}
        onBack={onBackPress}
      />
      <DrawerBody>
        <div className="sui-pb-4">
          <Typography>
            Use your existing lists to jumpstart this one. Simply copy entire lists or click the arrow to select specific items.
          </Typography>
        </div>
        <QueryProvider
          cacheKey={'copy-list'}
          defaultVariables={{
            isBrandPricingPolicyCompliant: true,
            loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership
          }}
        >
          <HoistedAnalyticsPriceFetcher
            selectedListItems={selectedListItems}
            updateBasePrice={updateBasePrice}
          >
            {
              screen === SCREENS.COPY_LIST_SUMMARY && (
                <MiniListsSummary
                  onListClick={onListClick}
                  selectedListItems={selectedListItems}
                  onItemSelectAll={onListSelectAll}
                  currentListId={listId}
                  lists={lists}
                  totalResults={totalResults}
                  loading={listsLoading}
                  setPage={setPage}
                  onItemsWithPricingChange={setItemsWithPricing}
                />
              )
            }
            {
              screen === SCREENS.COPY_LIST_PRODUCTS && (
                <MiniProductList
                  listId={selectedList?.listId}
                  totalItems={selectedList?.totalItems}
                  onItemSelect={onItemSelectionChange}
                  onItemSelectAll={onListSelectAll}
                  selectedItems={selectedListItems[selectedList?.listId] || []}
                  totalItemsSelected={totalItemsSelected}
                />
              )
            }
          </HoistedAnalyticsPriceFetcher>
        </QueryProvider>

        <AbandonSelectionWarning
          open={abandonWarning?.show}
          handleClose={() => setAbandonWarning({ show: false, action: '' })}
          onConfirm={(__event) => {
            if (abandonWarning?.action === 'CLOSE') {
              onDrawerClose(__event, true);
            } else if (abandonWarning?.action === 'BACK') {
              onBackPress(__event, true);
            }
            setAbandonWarning({ show: false, action: '' })
          }}
        />
      </DrawerBody>
      <DrawerFooter>
        <LoadingButton
          data-testid="copy-list-add-button"
          variant="primary"
          fullWidth
          onClick={handleSubmit}
          type="submit"
          loading={showButtonLoading}
        >
          Add {totalItemsSelected} Items
        </LoadingButton>
      </DrawerFooter>
    </>
  );
};

CopyList.displayName = 'CopyList';
CopyList.propTypes = {
  listId: string.isRequired,
  onClose: func.isRequired,
  onMaterialsAdded: func.isRequired
};
CopyList.defaultProps = {};
CopyList.dataModel = extend({
  lists: params({ page: numberType(), pageSize: numberType() }).shape({
    pagination: shapeType({
      pageSize: numberType(),
      totalResults: numberType(),
      totalPages: numberType(),
      hasMore: boolType()
    }),
    results: arrayOfType({
      id: stringType(),
      name: stringType(),
      url: stringType(),
      itemCount: numberType(),
      listAccessType: stringType(),
      images: arrayOfType(shapeType({
        url: stringType()
      }))
    })
  })
}, updateListModel);
